import type { CSSProperties } from 'react';

interface IconProps {
  size?: number;
  style?: CSSProperties;
  className?: string;
}

const base = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
});

export const TodayIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <path d="M11.5 4.6A5.5 5.5 0 1 0 5 12.9" />
    <rect x="7" y="11" width="15" height="9" rx="4.5" />
  </svg>
);

export const CalendarIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <rect x="2.5" y="4" width="18" height="17" rx="4" />
    <path d="M2.5 9.5h18M8 2v4M15 2v4" />
  </svg>
);

export const ClockIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <circle cx="12" cy="13" r="8" />
    <path d="M12 9v4l2.5 2.5M4.5 4.5 7 2.5M19.5 4.5 17 2.5" />
  </svg>
);

export const PrayerIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <path d="M15.5 3.5A8.5 8.5 0 1 0 20.5 15 6.8 6.8 0 0 1 15.5 3.5Z" />
    <path d="m18 6.2.6 1.3 1.4.2-1 1 .2 1.4-1.2-.7-1.2.7.2-1.4-1-1 1.4-.2Z" />
  </svg>
);

export const QuranIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <path d="M12 6.5C10 5 7 4.5 3 5v13c4-.5 7 0 9 1.5 2-1.5 5-2 9-1.5V5c-4-.5-7 0-9 1.5Z" />
    <path d="M12 6.5v13" />
  </svg>
);

export const ExchangeIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <path d="M4 8h14l-3.5-3.5M20 16H6l3.5 3.5" />
  </svg>
);

export const KanjiIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <rect x="2.5" y="2.5" width="19" height="19" rx="5" />
    <text x="12" y="16.5" textAnchor="middle" fontSize="12" fill="currentColor" stroke="none" fontFamily="Hiragino Sans, sans-serif">
      漢
    </text>
  </svg>
);

export const ScenesIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <rect x="2.5" y="2.5" width="7.5" height="7.5" rx="2" />
    <rect x="14" y="2.5" width="7.5" height="7.5" rx="2" />
    <rect x="2.5" y="14" width="7.5" height="7.5" rx="2" />
    <rect x="14" y="14" width="7.5" height="7.5" rx="2" />
  </svg>
);

export const SettingsIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <circle cx="12" cy="12" r="9.5" />
    <circle cx="12" cy="12" r="3.2" />
  </svg>
);

export type WeatherKind = 'sun' | 'partly' | 'cloud' | 'rain' | 'snow' | 'fog' | 'storm';

/** Weather glyph in the prototype's outline style; `sunFill` and `cloudFill` come from the theme. */
export function WeatherIcon({ kind, size = 26, isDay = true }: { kind: WeatherKind | string; size?: number; isDay?: boolean }) {
  const sun = { stroke: 'var(--sun)', fill: 'var(--sunFill)' };
  const cloud = { stroke: 'var(--ink)', fill: 'var(--cloudFill)' };
  const sw = 1.6;
  const body = (() => {
    switch (kind) {
      case 'sun':
        return isDay ? (
          <circle cx="12" cy="12" r="6" strokeWidth={sw} {...sun} />
        ) : (
          <path d="M15 4a8 8 0 1 0 5 13A7 7 0 0 1 15 4z" strokeWidth={sw} stroke="var(--ink)" fill="none" />
        );
      case 'partly':
        return (
          <>
            <circle cx="9" cy="8" r="5" strokeWidth={sw} {...sun} />
            <rect x="7" y="11" width="15" height="9" rx="4.5" strokeWidth={sw} {...cloud} />
          </>
        );
      case 'cloud':
        return <rect x="3" y="7" width="18" height="10" rx="5" strokeWidth={sw} stroke="var(--ink)" fill="none" />;
      case 'fog':
        return <path d="M3 9h18M5 13h14M3 17h18" strokeWidth={sw} stroke="var(--ink)" />;
      case 'rain':
      case 'snow':
      case 'storm':
        return (
          <>
            <rect x="3" y="3" width="18" height="10" rx="5" strokeWidth={sw} stroke="var(--ink)" fill="none" />
            {kind === 'storm' ? (
              <path d="M13 14l-3 4h4l-3 4" strokeWidth={sw} stroke="var(--now)" />
            ) : (
              [7, 12, 17].map((x) => (
                <circle key={x} cx={x} cy="19" r={kind === 'snow' ? 1.6 : 1.4} fill="var(--rain)" stroke="none" />
              ))
            )}
          </>
        );
      default:
        return <rect x="3" y="7" width="18" height="10" rx="5" strokeWidth={sw} stroke="var(--ink)" fill="none" />;
    }
  })();
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {body}
    </svg>
  );
}

export function SceneIcon({ icon, size = 34 }: { icon: 'sun' | 'moon' | 'leaf' | 'grid' | 'dash' | 'home'; size?: number }) {
  return (
    <svg {...base(size)} strokeWidth={1.8}>
      {icon === 'sun' && <circle cx="12" cy="12" r="6" />}
      {icon === 'moon' && <path d="M15 3a9 9 0 1 0 6 14A8 8 0 0 1 15 3z" />}
      {icon === 'leaf' && <path d="M4 20C4 9 9 4 20 4c0 11-5 16-16 16zM4 20l9-9" />}
      {icon === 'home' && <path d="M3.5 11 12 4l8.5 7M6 9.5V20h12V9.5M10 20v-5.5h4V20" />}
      {icon === 'dash' && (
        <>
          <rect x="2.5" y="2.5" width="19" height="4.5" rx="2" />
          <rect x="2.5" y="9.5" width="11" height="6" rx="2" />
          <rect x="15.5" y="9.5" width="6" height="6" rx="2" />
          <path d="M2.5 18.5h4M9 18.5h3M14.5 18.5h3M19.5 18.5h2" />
        </>
      )}
      {icon === 'grid' && (
        <>
          <rect x="3" y="3" width="7" height="7" rx="2" />
          <rect x="14" y="3" width="7" height="7" rx="2" />
          <rect x="3" y="14" width="7" height="7" rx="2" />
          <rect x="14" y="14" width="7" height="7" rx="2" />
        </>
      )}
    </svg>
  );
}

export const TasksIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <rect x="2.5" y="3.5" width="6" height="6" rx="1.5" />
    <path d="M4 6.5l1.3 1.3L7.6 5.2M12 6.5h9.5M12 16.5h9.5" />
    <rect x="2.5" y="13.5" width="6" height="6" rx="1.5" />
  </svg>
);

export const HabitsIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...base(size)} {...p}>
    <circle cx="12" cy="12" r="9.5" />
    <path d="M7.5 12.5l3 3 6-6.5" />
  </svg>
);
